import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuth } from "@/hooks/use-auth";
import {
  getDeviceCategory,
  getPresenceSessionId,
  shortUserTag,
  type PresenceMeta,
} from "@/lib/presence";

const CHANNEL = "live-visitors";

type PresenceState = {
  connected: boolean;
  visitors: PresenceMeta[];
  total: number;
  members: number;
  guests: number;
};

const PresenceContext = createContext<PresenceState>({
  connected: false,
  visitors: [],
  total: 0,
  members: 0,
  guests: 0,
});

/**
 * Global presence tracker. Mounted once at the root, so client-side navigation
 * updates the tracked path instead of creating a second presence entry.
 */
export function PresenceProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [connected, setConnected] = useState(false);
  const [visitors, setVisitors] = useState<PresenceMeta[]>([]);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const metaRef = useRef<PresenceMeta | null>(null);
  const pathRef = useRef(path);
  pathRef.current = path;

  const identity = loading ? null : user ? (user.is_anonymous ? "guest" : "member") : "guest";
  const userTag = !loading && user && !user.is_anonymous ? shortUserTag(user.id) : null;

  useEffect(() => {
    if (typeof window === "undefined" || identity === null) return;

    let cancelled = false;
    let cleanup: (() => void) | null = null;

    const start = async () => {
      // Nothing to connect to while offline — avoids the DNS failure loop.
      if (cancelled || navigator.onLine === false) return;
      const supabase = await getSupabase();
      if (cancelled) return;

      const sessionId = getPresenceSessionId();
      const connectedSince = new Date().toISOString();
      const buildMeta = (): PresenceMeta => ({
        sessionId,
        kind: identity,
        userTag,
        path: pathRef.current,
        device: getDeviceCategory(),
        connectedSince,
        updatedAt: new Date().toISOString(),
      });

      const channel = supabase.channel(CHANNEL, {
        config: { presence: { key: sessionId } },
      });
      channelRef.current = channel;

      const sync = () => {
        const state = channel.presenceState<PresenceMeta>();
        // One presence key == one browser session, so keys already dedupe pages/tabs.
        const list = Object.values(state)
          .map((entries) => entries[entries.length - 1])
          .filter((entry): entry is PresenceMeta & { presence_ref: string } =>
            Boolean(entry?.sessionId),
          );
        setVisitors(list.map(({ presence_ref: _ref, ...rest }) => rest as PresenceMeta));
      };

      let failures = 0;
      channel
        .on("presence", { event: "sync" }, sync)
        .on("presence", { event: "join" }, sync)
        .on("presence", { event: "leave" }, sync)
        .subscribe(async (status) => {
          if (status === "SUBSCRIBED") {
            failures = 0;
            metaRef.current = buildMeta();
            await channel.track(metaRef.current);
            setConnected(true);
          } else if (status === "CLOSED" || status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            setConnected(false);
            failures += 1;
            // Give up instead of retrying a socket that cannot resolve — a few
            // failures in a row means realtime is unreachable on this network.
            if (failures >= 3) {
              channelRef.current = null;
              void supabase.removeChannel(channel);
            }
          }
        });

      const handleUnload = () => {
        void channel.untrack();
      };
      window.addEventListener("pagehide", handleUnload);

      cleanup = () => {
        window.removeEventListener("pagehide", handleUnload);
        channelRef.current = null;
        metaRef.current = null;
        setConnected(false);
        void channel.untrack().finally(() => {
          void supabase.removeChannel(channel);
        });
      };
    };

    // Presence is never needed for the first paint, so the websocket waits
    // until the browser is idle after the page has rendered.
    const ric = (window as Window & typeof globalThis).requestIdleCallback;
    let idleId: number | null = null;
    let timerId: number | null = null;
    if (typeof ric === "function") {
      idleId = ric(() => void start(), { timeout: 5000 });
    } else {
      timerId = window.setTimeout(() => void start(), 2500);
    }

    return () => {
      cancelled = true;
      if (idleId !== null) window.cancelIdleCallback?.(idleId);
      if (timerId !== null) window.clearTimeout(timerId);
      cleanup?.();
    };
  }, [identity, userTag]);


  // Navigation updates the existing presence entry — never adds a new visitor.
  useEffect(() => {
    const channel = channelRef.current;
    const meta = metaRef.current;
    if (!channel || !meta || !connected) return;
    metaRef.current = { ...meta, path, updatedAt: new Date().toISOString() };
    void channel.track(metaRef.current);
  }, [path, connected]);

  const value = useMemo<PresenceState>(() => {
    const members = visitors.filter((v) => v.kind === "member").length;
    return {
      connected,
      visitors,
      total: visitors.length,
      members,
      guests: visitors.length - members,
    };
  }, [connected, visitors]);

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

export function useLivePresence(): PresenceState {
  return useContext(PresenceContext);
}
